import { useState } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, CalendarClock, Loader2, Repeat, Star } from 'lucide-react';
import { Button } from '@/components/atoms/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/atoms/card';
import { Input } from '@/components/atoms/input';
import { Switch } from '@/components/atoms/switch';
import { PublicLayout } from '@/components/public/PublicLayout';
import {
  publicDelete,
  publicGet,
  publicPost,
  publicPut,
  toPublicApiUrl,
} from '@/lib/api/publicClient';
import { StripePaymentMethodSetupForm } from '@/components/organisms/payments/StripePaymentMethodSetupForm';
import { PayabliPaymentMethodSetupForm } from '@/components/organisms/payments/PayabliPaymentMethodSetupForm';
import { recurringPortalEndpoints } from '@/lib/api/endpoints';
import {
  TOO_MANY_ATTEMPTS_MESSAGE,
  isTooManyAttempts,
  publicRefusalMessage,
  publicSaveError,
} from '@/lib/api/public-refusal';
import {
  dateStringToDate,
  formatDateTimeInTimeZone,
  timeKeyInTimeZone,
  zonedDateTimeToUtc,
} from '@/lib/date-values';
import { recurrenceSummary } from '@/lib/jobs/recurrenceSummary';
import {
  formatPlanPrice,
  recurringPlanStatus,
  type RecurringPlanPrice,
} from '@/lib/portal/recurringPlan';
import type { RecurringConfig } from '@/types/job';

export const Route = createFileRoute('/(public)/recurring/$token')({
  component: RecurringPortalPage,
});

interface PortalGates {
  reschedule: boolean;
  skip: boolean;
  pause: boolean;
  cancel: boolean;
}
interface PortalService {
  jobId: string;
  jobNumber: string;
  name: string;
  status: string;
  cadence: RecurringConfig | null;
  startDate: string | null;
  endDate: string | null;
  nextVisitDate: string | null;
  /** The plan's own price; null for a per-visit series with none. */
  price: RecurringPlanPrice | null;
  minimumTermMonths: number | null;
  cancellationFee: number | null;
  inMinimumTerm: boolean;
}
interface PortalContext {
  company: { name: string; timezone: string };
  customer: { name: string };
  gates: PortalGates;
  services: PortalService[];
}
interface PortalVisitCompletion {
  photos: string[];
  receipts: { label: string; url: string }[];
}
interface PortalVisit {
  appointmentId: string;
  jobId: string;
  label: string;
  serviceName: string | null;
  status: string;
  startAt: string | null;
  endAt: string | null;
  scheduledDate: string | null;
  timezone: string | null;
  completion?: PortalVisitCompletion;
}
interface PortalVisits {
  upcoming: PortalVisit[];
  history: PortalVisit[];
}
interface ReviewPrefs {
  reviewRequests: boolean;
  sms: boolean;
  email: boolean;
}

/** Why a self-service action failed, as the server said it; else `fallback`. */
function actionErrorMessage(error: unknown, fallback: string): string {
  if (isTooManyAttempts(error)) return TOO_MANY_ATTEMPTS_MESSAGE;
  return error instanceof Error && error.message.trim() ? error.message : fallback;
}

function money(n: number): string {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(n);
}
const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
// Contract dates are DATE values serialized at UTC midnight. Format their date
// components directly so they remain the same calendar day in every timezone.
function dateOnlyLabel(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return `${WEEKDAYS[d.getUTCDay()]}, ${MONTHS[d.getUTCMonth()]} ${d.getUTCDate()}`;
}

function visitDateLabel(visit: PortalVisit, companyTimezone: string): string {
  if (visit.scheduledDate) return dateOnlyLabel(visit.scheduledDate);
  return (
    formatDateTimeInTimeZone(visit.startAt, visit.timezone || companyTimezone, {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
    }) || '—'
  );
}

function RecurringPortalPage() {
  const { token } = Route.useParams();
  const qc = useQueryClient();

  const ctxQuery = useQuery({
    queryKey: ['portal', token, 'context'],
    queryFn: () => publicGet<PortalContext>(recurringPortalEndpoints.context(token)),
    retry: false,
  });
  const visitsQuery = useQuery({
    queryKey: ['portal', token, 'visits'],
    queryFn: () => publicGet<PortalVisits>(recurringPortalEndpoints.visits(token)),
    enabled: ctxQuery.isSuccess,
  });
  const prefsQuery = useQuery({
    queryKey: ['portal', token, 'prefs'],
    queryFn: () => publicGet<ReviewPrefs>(recurringPortalEndpoints.reviewPreferences(token)),
    enabled: ctxQuery.isSuccess,
  });

  const invalidate = () => {
    void qc.invalidateQueries({ queryKey: ['portal', token] });
  };

  if (ctxQuery.isLoading) {
    return (
      <PublicLayout maxWidth="lg">
        <div className="flex items-center justify-center py-20 text-gray-500">
          <Loader2 className="mr-2 size-5 animate-spin" /> Loading your services…
        </div>
      </PublicLayout>
    );
  }
  if (ctxQuery.isError || !ctxQuery.data) {
    return (
      <PublicLayout maxWidth="lg">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <AlertTriangle className="size-5 text-amber-500" /> Link unavailable
            </CardTitle>
            <CardDescription>
              This portal link is invalid or has expired. Please contact your service provider for a
              new link.
            </CardDescription>
          </CardHeader>
        </Card>
      </PublicLayout>
    );
  }

  const ctx = ctxQuery.data;

  return (
    <PublicLayout maxWidth="lg">
      <header className="mb-6">
        <p className="text-sm font-medium text-gray-500">{ctx.company.name}</p>
        <h1 className="text-2xl font-bold text-gray-900">Welcome back, {ctx.customer.name}</h1>
        <p className="mt-1 text-sm text-gray-600">
          Manage your recurring services, visits, and preferences.
        </p>
      </header>

      {/* Recurring services */}
      <section className="mb-8 space-y-4">
        <h2 className="flex items-center gap-2 text-lg font-semibold text-gray-900">
          <Repeat className="size-5" /> Your recurring services
        </h2>
        {ctx.services.length === 0 ? (
          <Card>
            <CardContent className="py-8 text-center text-sm text-gray-500">
              You have no recurring services yet.
            </CardContent>
          </Card>
        ) : (
          ctx.services.map((svc) => (
            <ServiceCard
              key={svc.jobId}
              token={token}
              svc={svc}
              gates={ctx.gates}
              companyTimezone={ctx.company.timezone}
              onChange={invalidate}
            />
          ))
        )}
      </section>

      {/* Upcoming visits */}
      <section className="mb-8 space-y-4">
        <h2 className="flex items-center gap-2 text-lg font-semibold text-gray-900">
          <CalendarClock className="size-5" /> Upcoming visits
        </h2>
        {visitsQuery.isLoading ? (
          <Card>
            <CardContent className="py-6 text-center text-sm text-gray-500">Loading…</CardContent>
          </Card>
        ) : (visitsQuery.data?.upcoming.length ?? 0) === 0 ? (
          <Card>
            <CardContent className="py-6 text-center text-sm text-gray-500">
              No upcoming visits in the next 90 days.
            </CardContent>
          </Card>
        ) : (
          visitsQuery.data!.upcoming.map((v) => (
            <VisitCard
              key={v.appointmentId}
              token={token}
              visit={v}
              gates={ctx.gates}
              companyTimezone={ctx.company.timezone}
              onChange={invalidate}
            />
          ))
        )}

        {(visitsQuery.data?.history.length ?? 0) > 0 && (
          <details className="rounded-lg border bg-white p-4">
            <summary className="cursor-pointer text-sm font-medium text-gray-700">
              Visit history ({visitsQuery.data!.history.length})
            </summary>
            <ul className="mt-3 space-y-2">
              {visitsQuery.data!.history.map((v) => (
                <li key={v.appointmentId} className="text-sm text-gray-600">
                  <VisitHistoryRow visit={v} companyTimezone={ctx.company.timezone} />
                </li>
              ))}
            </ul>
          </details>
        )}
      </section>

      {/* Payment methods (§13 / M8) */}
      <PaymentMethodsSection token={token} />

      {/* Review preferences */}
      {prefsQuery.data && <ReviewPrefsCard token={token} prefs={prefsQuery.data} />}
    </PublicLayout>
  );
}

interface PortalPaymentMethod {
  id: string;
  /**
   * Rail of the saved method ('card' / 'CREDIT_CARD' / 'ACH' / 'us_bank_account',
   * depending on the processor). The portal can save a BANK ACCOUNT now that the
   * embedded form offers the bank rail, so the list must stop calling every row
   * a card.
   */
  type?: string | null;
  brand?: string | null;
  last4?: string | null;
  expMonth?: number | null;
  expYear?: number | null;
}

/** A saved bank account, whatever the processor calls it. */
function isBankMethod(method: PortalPaymentMethod): boolean {
  return /ach|bank|echeck/i.test(method.type ?? '');
}
interface PortalPaymentMethods {
  defaultPaymentMethodId: string | null;
  paymentMethods: PortalPaymentMethod[];
  /** Payor identifier the Payabli embedded component requires. */
  contactId?: string;
  /** Processor whose add-card form the portal must render. */
  provider?: 'STRIPE' | 'PAYABLI';
  /**
   * Rails the tenant accepts. REQUIRED — the embedded component enables both
   * unless told otherwise, so an absent value must fail closed.
   */
  acceptCard: boolean;
  acceptACH: boolean;
  /** Browser-safe embedded-component config; present when provider is PAYABLI. */
  payabli?: { entryPoint: string | null; publicToken: string | null; mode: 'TEST' | 'LIVE' };
}
interface PortalSetupIntent {
  clientSecret: string;
  publishableKey: string;
  accountId?: string | null;
}

/**
 * Saved payment methods for the recurring portal. Exported for test: the list
 * has to name a saved BANK ACCOUNT correctly now that the embedded form offers
 * the bank rail.
 */
export function PaymentMethodsSection({ token }: { token: string }) {
  const qc = useQueryClient();
  const [adding, setAdding] = useState(false);

  const pmQuery = useQuery({
    queryKey: ['portal', token, 'payment-methods'],
    queryFn: () => publicGet<PortalPaymentMethods>(recurringPortalEndpoints.paymentMethods(token)),
  });
  const setupIntent = useMutation({
    mutationFn: () =>
      publicPost<PortalSetupIntent>(recurringPortalEndpoints.paymentMethodSetupIntent(token)),
  });
  const refresh = () => qc.invalidateQueries({ queryKey: ['portal', token, 'payment-methods'] });

  const syncMutation = useMutation({
    mutationFn: (setDefaultId?: string) =>
      publicPost(recurringPortalEndpoints.paymentMethods(token), {
        setDefaultId,
      }),
    onSuccess: () => void refresh(),
  });
  const removeMutation = useMutation({
    mutationFn: (pmId: string) =>
      publicDelete(recurringPortalEndpoints.removePaymentMethod(token, pmId)),
    onSuccess: () => void refresh(),
  });
  // Payabli add-card: the embedded component yields a short-lived temp token
  // the backend exchanges + stores as a canonical saved method.
  const tokenizeMutation = useMutation({
    mutationFn: (body: Record<string, unknown>) =>
      publicPost(recurringPortalEndpoints.paymentMethodTokenize(token), body),
    onSuccess: () => void refresh(),
  });

  const methods = pmQuery.data?.paymentMethods ?? [];
  const provider = pmQuery.data?.provider;
  const payabliConfig = pmQuery.data?.payabli;
  const contactId = pmQuery.data?.contactId;
  // Fail CLOSED: an absent flag disables the rail rather than re-opening one
  // the business turned off. The tokenize endpoint enforces the same rails
  // server-side, so this is the UI half only.
  const cardEnabled = pmQuery.data?.acceptCard === true;
  const achEnabled = pmQuery.data?.acceptACH === true;

  return (
    <section className="mb-8 space-y-4">
      <h2 className="text-lg font-semibold text-gray-900">Payment methods</h2>
      <Card>
        <CardContent className="space-y-3 py-4">
          {methods.length === 0 ? (
            <p className="text-sm text-gray-500">No saved payment methods yet.</p>
          ) : (
            <ul className="space-y-2">
              {methods.map((m) => (
                <li
                  key={m.id}
                  className="flex items-center justify-between rounded border px-3 py-2 text-sm"
                >
                  <span className="capitalize">
                    {m.brand ?? (isBankMethod(m) ? 'bank account' : 'card')} ••••{' '}
                    {m.last4 ?? '????'}
                    {m.expMonth && m.expYear ? ` · ${m.expMonth}/${m.expYear}` : ''}
                    {pmQuery.data?.defaultPaymentMethodId === m.id ? (
                      <span className="ml-2 rounded bg-emerald-100 px-1.5 py-0.5 text-xs text-emerald-700">
                        Default
                      </span>
                    ) : null}
                  </span>
                  <span className="flex gap-3">
                    {pmQuery.data?.defaultPaymentMethodId !== m.id ? (
                      <button
                        type="button"
                        className="text-blue-600 hover:underline"
                        onClick={() => syncMutation.mutate(m.id)}
                      >
                        Make default
                      </button>
                    ) : null}
                    <button
                      type="button"
                      aria-label="Remove payment method"
                      className="text-gray-400 hover:text-destructive"
                      onClick={() => removeMutation.mutate(m.id)}
                    >
                      Remove
                    </button>
                  </span>
                </li>
              ))}
            </ul>
          )}
          {removeMutation.isError ? (
            <p className="text-sm text-destructive">
              {actionErrorMessage(
                removeMutation.error,
                'We couldn’t remove that payment method. Please contact your service provider.',
              )}
            </p>
          ) : null}

          {/* Branch explicitly on the tenant's processor, and only once the
              query has settled on one. Rendering the Stripe CTA while
              `provider` is still unknown lets a Payabli customer click through
              to /setup-intent, which the backend rejects by design — the
              button would just look dead. */}
          {pmQuery.isPending ? (
            <p className="text-sm text-gray-500">Loading payment methods…</p>
          ) : provider === 'PAYABLI' ? (
            !payabliConfig?.publicToken ||
            !payabliConfig.entryPoint ||
            !contactId ||
            (!cardEnabled && !achEnabled) ? (
              <p className="text-sm text-destructive">
                Adding a payment method isn&apos;t available right now. Please contact your service
                provider.
              </p>
            ) : adding ? (
              <div className="space-y-3 pt-1">
                {/* No name field of our own: the component asks for the name
                    on the card or account, and the server reads it back. */}
                <PayabliPaymentMethodSetupForm
                  appearance="customer"
                  publicToken={payabliConfig.publicToken}
                  entryPoint={payabliConfig.entryPoint}
                  mode={payabliConfig.mode}
                  // Named for the rail the payer is on: the bank rail is
                  // reachable from the form's own Card | Bank switch.
                  submitLabel={(rail) => (rail === 'ach' ? 'Save bank account' : 'Save card')}
                  methods={{ card: cardEnabled, ach: achEnabled }}
                  // Paypoints that require a customer identifier reject
                  // tokenization client-side without a payor; customerNumber
                  // must be the contact id (the backend exchange binds it).
                  customerData={{ customerNumber: contactId }}
                  onComplete={async (result) => {
                    try {
                      await tokenizeMutation.mutateAsync({
                        temporaryToken: result.temporaryToken,
                        type: result.type,
                        last4: result.last4 ?? '',
                        brand: result.brand ?? null,
                        expMonth: result.expMonth ?? null,
                        expYear: result.expYear ?? null,
                      });
                    } catch (error) {
                      throw publicSaveError(
                        error,
                        "We couldn't save your payment method. Please try again or contact your service provider.",
                      );
                    }
                    setAdding(false);
                  }}
                />
              </div>
            ) : (
              <Button variant="outline" size="sm" onClick={() => setAdding(true)}>
                {achEnabled ? 'Add payment method' : 'Add card'}
              </Button>
            )
          ) : provider !== 'STRIPE' ? (
            <p className="text-sm text-destructive">
              Adding a card isn&apos;t available right now. Please contact your service provider.
            </p>
          ) : adding && setupIntent.data ? (
            <div className="pt-1">
              <StripePaymentMethodSetupForm
                clientSecret={setupIntent.data.clientSecret}
                publishableKey={setupIntent.data.publishableKey}
                stripeAccountId={setupIntent.data.accountId}
                submitLabel="Save card"
                onComplete={async () => {
                  try {
                    await syncMutation.mutateAsync(undefined);
                  } catch (error) {
                    // Stripe has already confirmed the setup by now; only our
                    // copy of the customer's methods failed to update.
                    throw publicSaveError(
                      error,
                      "Your payment method was accepted, but we couldn't finish saving it. Please refresh the page or contact your service provider.",
                    );
                  }
                  setAdding(false);
                  setupIntent.reset();
                }}
              />
            </div>
          ) : (
            <div className="space-y-2">
              <Button
                variant="outline"
                size="sm"
                disabled={setupIntent.isPending}
                onClick={() => {
                  // mutate (not mutateAsync) so a rejected setup-intent lands
                  // in `setupIntent.error` and is rendered below, instead of
                  // becoming an unhandled rejection with a dead-looking button.
                  setupIntent.mutate(undefined, {
                    onSuccess: () => setAdding(true),
                  });
                }}
              >
                {setupIntent.isPending ? 'Preparing…' : 'Add card'}
              </Button>
              {setupIntent.isError ? (
                <p className="text-sm text-destructive">
                  {/* Only a refusal the server wrote for the customer (e.g. Stripe
                      not fully configured) is shown as-is; other 4xx messages
                      can carry staff or Stripe detail, so they stay generic. */}
                  {isTooManyAttempts(setupIntent.error)
                    ? TOO_MANY_ATTEMPTS_MESSAGE
                    : (publicRefusalMessage(setupIntent.error) ?? (
                        <>
                          We couldn&apos;t start secure card entry. Please try again or contact your
                          service provider.
                        </>
                      ))}
                </p>
              ) : null}
            </div>
          )}
        </CardContent>
      </Card>
    </section>
  );
}

function VisitHistoryRow({
  visit,
  companyTimezone,
}: {
  visit: PortalVisit;
  companyTimezone: string;
}) {
  const c = visit.completion;
  const hasDetail = !!c && (c.photos.length > 0 || c.receipts.length > 0);

  const header = (
    <>
      <span>
        {visit.label}
        {visit.serviceName ? ` · ${visit.serviceName}` : ''}
      </span>
      <span className="tabular-nums">
        {visitDateLabel(visit, companyTimezone)} · {visit.status.toLowerCase()}
      </span>
    </>
  );

  if (!hasDetail) {
    return <div className="flex justify-between">{header}</div>;
  }

  return (
    <details className="group">
      <summary className="flex cursor-pointer list-none justify-between">{header}</summary>
      <div className="mt-2 space-y-2 border-l-2 border-gray-100 pl-3">
        {c!.photos.length > 0 && (
          <div className="flex flex-wrap gap-2">
            {c!.photos.map((url, i) => (
              <a key={i} href={url} target="_blank" rel="noreferrer">
                <img
                  src={url}
                  alt={`Visit photo ${i + 1}`}
                  className="h-16 w-16 rounded object-cover"
                />
              </a>
            ))}
          </div>
        )}
        {c!.receipts.length > 0 && (
          <ul className="space-y-1">
            {c!.receipts.map((r) => (
              <li key={r.url}>
                <a
                  href={toPublicApiUrl(r.url)}
                  target="_blank"
                  rel="noreferrer"
                  className="text-blue-600 underline"
                >
                  Receipt {r.label}
                </a>
              </li>
            ))}
          </ul>
        )}
      </div>
    </details>
  );
}

function ServiceCard({
  token,
  svc,
  gates,
  companyTimezone,
  onChange,
}: {
  token: string;
  svc: PortalService;
  gates: PortalGates;
  companyTimezone: string;
  onChange: () => void;
}) {
  const [confirmCancel, setConfirmCancel] = useState(false);
  const [pauseStart, setPauseStart] = useState('');
  const [pauseEnd, setPauseEnd] = useState('');
  const [showPause, setShowPause] = useState(false);

  const pause = useMutation({
    mutationFn: () =>
      publicPost(recurringPortalEndpoints.pause(token, svc.jobId), { pauseStart, pauseEnd }),
    onSuccess: () => {
      setShowPause(false);
      onChange();
    },
  });
  const cancel = useMutation({
    mutationFn: () => publicPost(recurringPortalEndpoints.cancel(token, svc.jobId), {}),
    onSuccess: () => {
      setConfirmCancel(false);
      onChange();
    },
  });

  const isEnded = svc.status === 'ENDED';
  // A pause can't end before it starts; say so before the server refuses it.
  const pauseBackwards = Boolean(pauseStart && pauseEnd && pauseEnd < pauseStart);

  return (
    <Card>
      <CardHeader>
        <div className="flex items-start justify-between gap-3">
          <div>
            <CardTitle className="text-base">{svc.name}</CardTitle>
            <CardDescription>
              {recurrenceSummary(svc.cadence)}
              {svc.price ? ` · ${formatPlanPrice(svc.price)}` : ''}
            </CardDescription>
          </div>
          <span
            className={`rounded-full px-2 py-0.5 text-xs font-medium ${
              svc.status === 'ACTIVE'
                ? 'bg-emerald-100 text-emerald-700'
                : svc.status === 'PAUSED' || svc.status === 'PAST_DUE' || svc.status === 'SUSPENDED'
                  ? 'bg-amber-100 text-amber-700'
                  : 'bg-gray-100 text-gray-600'
            }`}
          >
            {recurringPlanStatus(svc.status).label}
          </span>
        </div>
      </CardHeader>
      <CardContent className="space-y-3 text-sm">
        <div className="text-gray-600">
          {/* An instant: its day is the company's, not UTC's. */}
          Next visit:{' '}
          <span className="font-medium">
            {svc.nextVisitDate
              ? formatDateTimeInTimeZone(svc.nextVisitDate, companyTimezone, {
                  weekday: 'short',
                  month: 'short',
                  day: 'numeric',
                })
              : '—'}
          </span>
        </div>

        {/* Min-term disclosure */}
        {svc.inMinimumTerm && svc.cancellationFee != null && (
          <div className="flex items-start gap-2 rounded-md bg-amber-50 p-2 text-xs text-amber-800">
            <AlertTriangle className="mt-0.5 size-3.5 shrink-0" />
            <span>
              This service is within its {svc.minimumTermMonths}-month minimum term. Cancelling now
              incurs a {money(svc.cancellationFee)} early-termination fee.
            </span>
          </div>
        )}

        {!isEnded && (gates.pause || gates.cancel) && (
          <div className="flex flex-wrap gap-2 pt-1">
            {gates.pause && svc.status === 'ACTIVE' && (
              <Button size="sm" variant="outline" onClick={() => setShowPause((v) => !v)}>
                Pause service
              </Button>
            )}
            {gates.cancel && (
              <Button
                size="sm"
                variant="outline"
                className="text-red-600"
                onClick={() => setConfirmCancel(true)}
              >
                Cancel service
              </Button>
            )}
          </div>
        )}

        {showPause && gates.pause && (
          <div className="space-y-2 rounded-md border p-3">
            <div className="grid grid-cols-2 gap-2">
              <label className="text-xs text-gray-600">
                Pause from
                <Input
                  type="date"
                  value={pauseStart}
                  onChange={(e) => setPauseStart(e.target.value)}
                />
              </label>
              <label className="text-xs text-gray-600">
                Pause until
                <Input type="date" value={pauseEnd} onChange={(e) => setPauseEnd(e.target.value)} />
              </label>
            </div>
            {pauseBackwards ? (
              <p className="text-xs text-destructive">
                The pause has to end on or after the day it starts.
              </p>
            ) : null}
            <Button
              size="sm"
              disabled={!pauseStart || !pauseEnd || pauseBackwards || pause.isPending}
              onClick={() => pause.mutate()}
            >
              {pause.isPending && <Loader2 className="mr-1.5 size-3.5 animate-spin" />}
              Confirm pause
            </Button>
            {pause.isError ? (
              <p className="text-xs text-destructive">
                {actionErrorMessage(pause.error, 'We couldn’t pause this service.')}
              </p>
            ) : null}
          </div>
        )}

        {confirmCancel && gates.cancel && (
          <div className="space-y-2 rounded-md border border-red-200 bg-red-50 p-3 text-xs">
            <p className="text-red-800">
              Cancel this recurring service? Future visits will stop.
              {svc.inMinimumTerm && svc.cancellationFee != null
                ? ` A ${money(svc.cancellationFee)} early-termination fee applies.`
                : ''}
            </p>
            <div className="flex gap-2">
              <Button size="sm" variant="ghost" onClick={() => setConfirmCancel(false)}>
                Keep service
              </Button>
              <Button
                size="sm"
                className="bg-red-600 text-white hover:bg-red-700"
                disabled={cancel.isPending}
                onClick={() => cancel.mutate()}
              >
                {cancel.isPending && <Loader2 className="mr-1.5 size-3.5 animate-spin" />}
                Confirm cancel
              </Button>
            </div>
            {cancel.isError ? (
              <p className="text-destructive">
                {actionErrorMessage(cancel.error, 'We couldn’t cancel this service.')}
              </p>
            ) : null}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function VisitCard({
  token,
  visit,
  gates,
  companyTimezone,
  onChange,
}: {
  token: string;
  visit: PortalVisit;
  gates: PortalGates;
  companyTimezone: string;
  onChange: () => void;
}) {
  const [showReschedule, setShowReschedule] = useState(false);
  const [newDate, setNewDate] = useState('');

  const skip = useMutation({
    mutationFn: () => publicPost(recurringPortalEndpoints.skip(token, visit.appointmentId), {}),
    onSuccess: onChange,
  });
  const reschedule = useMutation({
    mutationFn: () => {
      const timezone = visit.timezone || companyTimezone;
      const startAt = zonedDateTimeToUtc(
        dateStringToDate(newDate),
        timeKeyInTimeZone(visit.startAt, timezone),
        timezone,
      );
      if (!startAt) throw new Error('Select a valid date');
      return publicPost(recurringPortalEndpoints.reschedule(token, visit.appointmentId), {
        startAt: startAt.toISOString(),
      });
    },
    onSuccess: () => {
      setShowReschedule(false);
      onChange();
    },
  });

  return (
    <Card>
      <CardContent className="space-y-2 py-4 text-sm">
        <div className="flex items-center justify-between">
          <div>
            <p className="font-medium text-gray-900">{visit.label}</p>
            {visit.serviceName && <p className="text-xs text-gray-500">{visit.serviceName}</p>}
          </div>
          <span className="text-right text-gray-600">{visitDateLabel(visit, companyTimezone)}</span>
        </div>
        {(gates.reschedule || gates.skip) && (
          <div className="flex gap-2">
            {gates.reschedule && (
              <Button size="sm" variant="outline" onClick={() => setShowReschedule((v) => !v)}>
                Reschedule
              </Button>
            )}
            {gates.skip && (
              <Button
                size="sm"
                variant="ghost"
                disabled={skip.isPending}
                onClick={() => skip.mutate()}
              >
                {skip.isPending && <Loader2 className="mr-1.5 size-3.5 animate-spin" />}
                Skip
              </Button>
            )}
          </div>
        )}
        {showReschedule && gates.reschedule && (
          <div className="flex items-end gap-2 rounded-md border p-3">
            <label className="text-xs text-gray-600">
              New date
              <Input type="date" value={newDate} onChange={(e) => setNewDate(e.target.value)} />
            </label>
            <Button
              size="sm"
              disabled={!newDate || reschedule.isPending}
              onClick={() => reschedule.mutate()}
            >
              {reschedule.isPending && <Loader2 className="mr-1.5 size-3.5 animate-spin" />}
              Confirm
            </Button>
          </div>
        )}
        {/* Surface WHY an action failed (±window, optimizer-owned route visit,
            …) — a silently-ignored click reads as a broken button. */}
        {skip.isError || reschedule.isError ? (
          <p className="text-xs text-destructive">
            {(reschedule.error ?? skip.error) instanceof Error
              ? ((reschedule.error ?? skip.error) as Error).message
              : 'We couldn’t update this visit. Please contact your service provider.'}
          </p>
        ) : null}
      </CardContent>
    </Card>
  );
}

function ReviewPrefsCard({ token, prefs }: { token: string; prefs: ReviewPrefs }) {
  const qc = useQueryClient();
  const update = useMutation({
    mutationFn: (patch: Partial<ReviewPrefs>) =>
      publicPut<ReviewPrefs>(recurringPortalEndpoints.reviewPreferences(token), patch),
    onSuccess: (data) => {
      qc.setQueryData(['portal', token, 'prefs'], data);
    },
  });

  const Row = ({
    label,
    description,
    value,
    onToggle,
  }: {
    label: string;
    description: string;
    value: boolean;
    onToggle: (v: boolean) => void;
  }) => (
    <div className="flex items-center justify-between py-2">
      <div>
        <p className="text-sm font-medium text-gray-900">{label}</p>
        <p className="text-xs text-gray-500">{description}</p>
      </div>
      <Switch checked={value} onCheckedChange={onToggle} disabled={update.isPending} />
    </div>
  );

  return (
    <section className="mb-8">
      <h2 className="mb-3 flex items-center gap-2 text-lg font-semibold text-gray-900">
        <Star className="size-5" /> Communication preferences
      </h2>
      <Card>
        <CardContent className="divide-y py-2">
          <Row
            label="Review requests"
            description="Receive a request to review your service after visits."
            value={prefs.reviewRequests}
            onToggle={(v) => update.mutate({ reviewRequests: v })}
          />
          <Row
            label="Text message updates"
            description="Visit reminders and follow-ups by SMS."
            value={prefs.sms}
            onToggle={(v) => update.mutate({ sms: v })}
          />
          <Row
            label="Email updates"
            description="Visit reminders and follow-ups by email."
            value={prefs.email}
            onToggle={(v) => update.mutate({ email: v })}
          />
        </CardContent>
      </Card>
    </section>
  );
}
